// src/Components/Luna/LunaEvolution.tsx
// The evolution moment. Full screen on the deep space surface: the old form
// sits on a pedestal of light, trembles while stardust gathers into it and
// the glow builds, a white flash, then the new form springs out with rings
// and a spray of sparks. One line ("Trisaur evolved into Tribeast!"), the
// line's stage track with the new stage lit, and "New in your collection",
// because every stage is its own Collection entry. Continue closes it, See
// collection goes to the Lunas tab of the Collection. Reduced motion: a
// plain cross fade to the final scene.
import { useT } from "../../utils/i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import Portal from "../Utils/Portal";
import SafeImg from "../Ui/SafeImg";
import Icon from "../Ui/Icon";
import Pill from "../Ui/Pill";
import { soundManager } from "../../utils/SoundManager";
import type { LunaCelebration } from "./lunaPowerStore";
import ShareButton from "../Share/ShareButton";

export interface StageTile {
  stage: number;
  name: string;
  image: string | null;
  collected: boolean;
}

interface Props {
  event: LunaCelebration;
  stages: StageTile[];
  onClose: () => void;
  onOpenCollection: () => void;
}

const GATHER = Array.from({ length: 12 }, (_, i) => {
  const a = (i / 12) * Math.PI * 2 + 0.3;
  const r = 150 + (i % 3) * 26;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r * 0.85, d: 0.45 + (i % 6) * 0.13, s: 7 + (i % 3) * 3 };
});
const SPRAY = Array.from({ length: 16 }, (_, i) => {
  const a = (i / 16) * Math.PI * 2;
  const r = 120 + (i % 4) * 26;
  return { x: Math.cos(a) * r, y: Math.sin(a) * r * 0.8 - 20, s: 6 + (i % 4) * 2, d: (i % 5) * 0.03 };
});

/** Timeline (seconds). */
const T_FLASH = 2.15;
const T_REVEAL = 2.35;

const LunaEvolution: React.FC<Props> = ({ event, stages, onClose, onOpenCollection }) => {
  const t = useT();
  const reduce = useReducedMotion();
  const [revealed, setRevealed] = useState(!!reduce);
  const [flash, setFlash] = useState(false);
  const continueRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (reduce) {
      soundManager.play("evolution");
      continueRef.current?.focus();
      return undefined;
    }
    const s1 = window.setTimeout(() => soundManager.play("evolution"), (T_FLASH - 0.5) * 1000);
    const fl = window.setTimeout(() => setFlash(true), T_FLASH * 1000);
    const flOff = window.setTimeout(() => setFlash(false), (T_REVEAL + 0.6) * 1000);
    const t = window.setTimeout(() => setRevealed(true), T_REVEAL * 1000);
    const s2 = window.setTimeout(() => soundManager.play("level_up"), (T_REVEAL + 0.1) * 1000);
    const f = window.setTimeout(() => continueRef.current?.focus(), (T_REVEAL + 1.1) * 1000);
    return () => {
      window.clearTimeout(s1);
      window.clearTimeout(fl);
      window.clearTimeout(flOff);
      window.clearTimeout(t);
      window.clearTimeout(s2);
      window.clearTimeout(f);
    };
  }, [reduce, event.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && revealed) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, revealed]);

  const line = `${event.fromName} evolved into ${event.name}!`;
  const enter = (delay: number) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
      : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.45, ease: "easeOut" } };

  const track = useMemo(() => stages.filter((s) => s.stage >= 1).sort((a, b) => a.stage - b.stage), [stages]);

  return (
    <Portal>
      <motion.div
        className={`luna-evo ${revealed ? "is-revealed" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="luna-evo-title"
        data-testid="luna-evolution"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: reduce ? 0.15 : 0.35 }}
      >
        <div className="luna-evo__bg" aria-hidden="true" />
        <div className="luna-evo__body">
          <motion.div className="mr-overline luna-evo__over" {...(reduce ? {} : { initial: { opacity: 0 }, animate: { opacity: revealed ? 0.85 : 1 }, transition: { duration: 0.4 } })}>
            {revealed ? t("luna.evolution") : t("luna.evolving", { name: event.fromName })}
          </motion.div>

          <div className="luna-evo__stage" aria-hidden="true">
            <span className="luna-evo__pedestal" />

            {!reduce && !revealed && (
              <>
                <motion.span
                  className="luna-evo__glow"
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: [0, 0.5, 1], scale: [0.6, 0.9, 1.35] }}
                  transition={{ duration: T_FLASH, times: [0, 0.5, 1], ease: "easeIn" }}
                />
                {GATHER.map((p, i) => (
                  <motion.span
                    key={i}
                    className="luna-evo__mote"
                    style={{ width: p.s, height: p.s }}
                    initial={{ x: p.x, y: p.y, opacity: 0, scale: 1 }}
                    animate={{ x: 0, y: 0, opacity: [0, 1, 0.9, 0], scale: [1, 1, 0.6, 0.2] }}
                    transition={{ delay: p.d, duration: 0.9, ease: "easeIn", repeat: 1, repeatDelay: 0.05 }}
                  />
                ))}
                <motion.div
                  className="luna-evo__form luna-evo__form--old"
                  initial={{ scale: 0.7, opacity: 0, y: 20 }}
                  animate={{
                    scale: [0.7, 1, 1, 1.04, 0.98, 1.06, 0.96, 1.1],
                    opacity: 1,
                    y: 0,
                    rotate: [0, 0, -3, 3, -4, 4, -5, 0],
                  }}
                  transition={{ duration: T_FLASH, times: [0, 0.2, 0.4, 0.55, 0.68, 0.8, 0.9, 1] }}
                >
                  <SafeImg src={event.fromImage || undefined} alt="" draggable={false} fallback={<Icon name="paw" size={64} />} />
                </motion.div>
              </>
            )}

            {flash && !reduce && (
              <motion.span
                className="luna-evo__flash"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 0.6, times: [0, 0.3, 1], ease: "easeOut" }}
              />
            )}

            {revealed && (
              <>
                {!reduce && (
                  <>
                    <motion.span className="luna-evo__ring" initial={{ scale: 0.3, opacity: 0.9 }} animate={{ scale: 2.6, opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
                    <motion.span className="luna-evo__ring luna-evo__ring--late" initial={{ scale: 0.3, opacity: 0.7 }} animate={{ scale: 2.1, opacity: 0 }} transition={{ delay: 0.18, duration: 1, ease: "easeOut" }} />
                    {SPRAY.map((p, i) => (
                      <motion.span
                        key={i}
                        className="luna-evo__spark"
                        style={{ width: p.s, height: p.s }}
                        initial={{ x: 0, y: 0, opacity: 0, scale: 1 }}
                        animate={{ x: p.x, y: p.y, opacity: [0, 1, 0], scale: [1, 1, 0.3] }}
                        transition={{ delay: p.d, duration: 1.1, ease: "easeOut" }}
                      />
                    ))}
                  </>
                )}
                <span className="luna-evo__halo" />
                <motion.div
                  className="luna-evo__form luna-evo__form--new"
                  data-testid="luna-evolution-new"
                  initial={reduce ? { opacity: 0 } : { scale: 0.35, opacity: 0, y: 24 }}
                  animate={reduce ? { opacity: 1 } : { scale: [0.35, 1.18, 0.95, 1], opacity: 1, y: 0 }}
                  transition={reduce ? { duration: 0.25 } : { duration: 0.8, times: [0, 0.55, 0.8, 1], ease: "easeOut" }}
                >
                  <SafeImg src={event.image || undefined} alt={event.name} draggable={false} fallback={<Icon name="paw" size={64} />} />
                </motion.div>
              </>
            )}
          </div>

          {revealed && (
            <>
              <motion.h2 id="luna-evo-title" className="luna-evo__line" data-testid="luna-evolution-title" {...enter(0.2)}>
                {line}
              </motion.h2>

              {track.length > 1 && (
                <motion.ol className="luna-evo__track" aria-label="Stages" {...enter(0.45)}>
                  {track.map((s, i) => (
                    <React.Fragment key={s.stage}>
                      {i > 0 && <li className={`luna-evo__link ${s.collected ? "is-on" : ""}`} aria-hidden="true" />}
                      <li className={`luna-evo__node ${s.collected ? "is-collected" : "is-locked"} ${s.stage === event.stage ? "is-new" : ""}`}>
                        <span className="luna-evo__nodeart">
                          <SafeImg src={s.image || undefined} alt="" draggable={false} fallback={<Icon name="paw" size={20} />} />
                        </span>
                        <span className="luna-evo__nodename">{s.collected ? s.name : "???"}</span>
                      </li>
                    </React.Fragment>
                  ))}
                </motion.ol>
              )}

              <motion.div {...enter(0.65)}>
                <Pill tone="accent" icon={<Icon name="sparkle" size={14} filled />} className="luna-evo__new" data-testid="luna-evolution-new-pill">
                  {t("luna.newInCollection")}
                </Pill>
              </motion.div>

              <motion.div className="luna-evo__actions" {...enter(0.85)}>
                <button ref={continueRef} type="button" className="mr-btn mr-btn--accent mr-btn--block" onClick={onClose} data-testid="luna-evolution-continue">
                  {t("lesson.continue")}
                </button>
                <ShareButton
                  block
                  variant="secondary"
                  label="Share"
                  before={onClose}
                  spec={{ kind: "evolution", from: { name: event.fromName, image: event.fromImage || null }, to: { name: event.name, image: event.image || null } }}
                  testId="luna-evolution-share"
                />
                <button type="button" className="mr-btn mr-btn--ghost mr-btn--block luna-evo__more" onClick={onOpenCollection}>
                  {t("luna.seeCollection")}
                </button>
              </motion.div>
            </>
          )}
        </div>
      </motion.div>
    </Portal>
  );
};

export default LunaEvolution;
